const MAX_DIMENSION = 512;
const MAX_BYTES = 500 * 1024;
const MAX_QUALITY = 0.92;
const MIN_QUALITY = 0.6;
const SCALE_STEP = 0.85;
const MAX_SCALE_PASSES = 3;

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();

    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('图片读取失败'));
    };
    image.src = url;
  });
}

function getScale(width: number, height: number) {
  const maxSide = Math.max(width, height);
  return maxSide > MAX_DIMENSION ? MAX_DIMENSION / maxSide : 1;
}

function drawToCanvas(
  source: CanvasImageSource,
  width: number,
  height: number
): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(width));
  canvas.height = Math.max(1, Math.round(height));

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('无法获取画布上下文');
  }

  ctx.drawImage(source, 0, 0, canvas.width, canvas.height);
  return canvas;
}

function canvasToJpegBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('图片压缩失败'));
          return;
        }
        resolve(blob);
      },
      'image/jpeg',
      quality
    );
  });
}

async function compressCanvas(canvas: HTMLCanvasElement, maxBytes: number): Promise<Blob> {
  let blob = await canvasToJpegBlob(canvas, MAX_QUALITY);
  if (blob.size <= maxBytes) {
    return blob;
  }

  let low = MIN_QUALITY;
  let high = MAX_QUALITY;
  let best = blob;

  for (let i = 0; i < 6; i += 1) {
    const mid = (low + high) / 2;
    const next = await canvasToJpegBlob(canvas, mid);
    if (next.size <= maxBytes) {
      best = next;
      low = mid;
    } else {
      best = next;
      high = mid;
    }
  }

  return best;
}

function scaleCanvas(canvas: HTMLCanvasElement, ratio: number) {
  const width = Math.max(1, Math.round(canvas.width * ratio));
  const height = Math.max(1, Math.round(canvas.height * ratio));
  return drawToCanvas(canvas, width, height);
}

function toJpegName(name: string) {
  const trimmed = name.trim();
  if (!trimmed) return 'upload.jpg';
  return trimmed.replace(/\.[^/.]+$/, '') + '.jpg';
}

export async function preprocessImage(file: File): Promise<File> {
  const image = await loadImage(file);
  const scale = getScale(image.width, image.height);
  let canvas = drawToCanvas(image, image.width * scale, image.height * scale);
  let blob = await compressCanvas(canvas, MAX_BYTES);

  let pass = 0;
  while (blob.size > MAX_BYTES && pass < MAX_SCALE_PASSES) {
    canvas = scaleCanvas(canvas, SCALE_STEP);
    blob = await compressCanvas(canvas, MAX_BYTES);
    pass += 1;
  }

  return new File([blob], toJpegName(file.name), {
    type: 'image/jpeg',
    lastModified: file.lastModified,
  });
}
